import { apm } from "@elastic/apm-rum";
import { mm } from "@inovitas/infra3dsdk";
import {
  IFeatureStyle,
  IFeatureStyleRule,
} from "@inovitas/infra3dsdk/dist/types/mm/featuretree/interfaces/IFeatureStyle";
import {
  AddOutlined,
  ColorLensOutlined,
  Delete,
  TableRowsOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  TextField,
  Typography,
} from "@mui/material";
import { debounce } from "lodash";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { monitor } from "shared/common/services/apm-logging";
import { defaultStore } from "shared/common/stores/defaultStore";
import { sidebarStore } from "shared/common/stores/sidebarStore";
import { Property } from "shared/ui/toolbar/tools/layertreeAndEdit/layerContent/addLayerItems/CreateLayer";
import layerShapes, { Shapes } from "./layerShapes";
import LayerViewPrototype from "./LayerViewPrototype";

export default function LayerSettings({
  itemSelected,
  infra3dstyles,
  propertyRows,
  setPropertyRows,
  checkIfCanSave,
  updateExportObject,
  editProperties = false,
}: {
  infra3dstyles: IFeatureStyle | undefined;
  propertyRows: Property[];
  setPropertyRows: Dispatch<SetStateAction<Property[]>>;
  checkIfCanSave: () => void;
  updateExportObject?: () => void;
  itemSelected?: any; // TODO: Type
  editProperties?: boolean;
}) {
  const { t } = useTranslation();

  const darkTheme = defaultStore((state) => state.darkTheme);

  const activeAddLayersListItem = sidebarStore(
    (state) => state.activeAddLayersListItem
  );
  const changeSaveButtonStateOnAddNewLayers = sidebarStore(
    (state) => state.changeSaveButtonStateOnAddNewLayers
  );

  const shapes: Shapes[] = layerShapes();

  const [layerDesign, setLayerDesign] = useState<JSX.Element | string>(
    "Waiting..."
  );
  const [showStyle, setShowStyle] = useState<boolean>(false);
  const [styles, setStyles] = useState<IFeatureStyleRule[] | undefined>(
    undefined
  );

  //change property row name
  const handlePropertyRowName = (value: string, index: number) => {
    propertyRows[index].name = value;
    propertyRows[index].key = value;
    // checkIfCanSave();
  };

  //change property row type
  const handlePropertyRowDataType = (value: string | number, index: number) => {
    propertyRows[index].datatype = value;
    checkIfCanSave();
  };

  //add new property row
  const addPropertyRow = () => {
    setPropertyRows([
      ...propertyRows,
      { key: "", name: "", datatype: "", editable: true },
    ]);
  };

  //delete property row
  const deletePropertyRow = (position: number) => {
    setPropertyRows(
      propertyRows.filter((item, index: number) => index !== position)
    );
  };

  //check if can save changes
  const handleCheck = () => {
    checkIfCanSave();
  };

  //disable save button while editing
  const blockSaveButtonOnFocus = () => {
    changeSaveButtonStateOnAddNewLayers(false);
  };

  //convert grom hex to rgb
  const hexToRgb = (hex: string, transparency: number): number[] => {
    hex = hex.replace("#", "");
    if (hex.length === 3) {
      hex = hex
        .split("")
        .map((char) => char + char)
        .join("");
    }

    const bigint = parseInt(hex, 16);
    const red = (bigint >> 16) & 255;
    const green = (bigint >> 8) & 255;
    const blue = bigint & 255;

    const r = red / 255;
    const g = green / 255;
    const b = blue / 255;
    return [r, g, b, transparency];
  };

  //convert from rgb to hex
  const rgbToHex = (r: number, g: number, b: number): string => {
    const toHex = (c: number) => {
      const hex = Math.round(c * 255).toString(16);
      return hex.length === 1 ? "0" + hex : hex;
    };

    const red = toHex(r);
    const green = toHex(g);
    const blue = toHex(b);

    return `#${red}${green}${blue}`;
  };

  //when color is changing than update view
  const handleColor = debounce((item: IFeatureStyleRule, value: string) => {
    if (item.symbolizer === "polygon") {
      item.params.fill = hexToRgb(value, item.params.fill[3]);
    } else {
      const old_stroke = item.params.stroke;
      item.params.stroke = hexToRgb(value, old_stroke ? old_stroke[3] : 1);
    }
    handleView();
  }, 100);

  //change point shape
  const handleShape = (item: any, value: number | string) => {
    // TODO: Type
    if (item.symbolizer === "point") {
      if (value !== -1) {
        if (item.params["font-marker"]) {
          item.params["font-marker"].name = value;
        } else {
          item.params["font-marker"] = {
            name: value,
            rotation: 0,
            fontsize: 20,
            anchor: ["center", "middle"],
          };
        }
      } else {
        delete item.params["font-marker"];
      }
    }
    handleView();
  };

  //change size of shape or lines bold value
  const handleSize = (item: IFeatureStyleRule, value: number) => {
    if (item.symbolizer === "point") {
      item.params.size = value;
    } else if (item.symbolizer === "line") {
      item.params["stroke-width"] = value;
    }
    handleView();
  };

  //handle color attribute transparency
  const handleTransparency = (item: IFeatureStyleRule, value: number) => {
    if (item.symbolizer === "polygon") {
      item.params.fill[3] = 1 - value / 100;
    } else {
      if (item.params.stroke) item.params.stroke[3] = 1 - value / 100;
    }
    handleView();
  };

  //update feature view
  const handleView = () => {
    if (!styles || !shapes) return;
    if (updateExportObject) updateExportObject();
    setLayerDesign(LayerViewPrototype({ shapes, styles }));
  };

  //update feature view
  useEffect(() => {
    handleView();
  }, [styles]);

  //set default style for different type of layers
  useEffect(() => {
    if (infra3dstyles) {
      const point = infra3dstyles.rules.filter(
        (rule) => rule.symbolizer === "point"
      )[0];
      const line = infra3dstyles.rules.filter(
        (rule) => rule.symbolizer === "line"
      )[0];
      const polygon = infra3dstyles.rules.filter(
        (rule) => rule.symbolizer === "polygon"
      )[0];

      const st = [];
      if (point) st.push(point);
      if (line) st.push(line);
      if (polygon) st.push(polygon);
      setStyles(st);
    }
  }, [infra3dstyles]);

  return (
    <Box display={"flex"} flexDirection={"column"} gap={1}>
      <Typography
        color={darkTheme ? "white" : "darkblue.main"}
        fontSize={20}
        fontWeight={600}
      >
        {t("create_layer.settings")}
      </Typography>
      <Box
        display={"flex"}
        sx={{ flexDirection: { xs: "column", md: "row" } }}
        gap={1}
      >
        <Box
          display={"flex"}
          alignItems={"center"}
          color={
            !showStyle
              ? "secondary.main"
              : darkTheme
                ? "white"
                : "darkblue.main"
          }
          paddingY={0.5}
          paddingX={1.5}
          gap={0.5}
          sx={{
            borderBottom: "2px transparent solid",
            borderColor: !showStyle ? "secondary.main" : "transparent",
            "@media (hover: hover)": {
              ":hover": {
                borderColor: "secondary.main",
                color: "secondary.main",
                cursor: "pointer",
              },
            },
          }}
          onClick={() => setShowStyle(false)}
        >
          <TableRowsOutlined fontSize="small" />
          <Typography
            textTransform={"uppercase"}
            fontSize={14}
            fontWeight={500}
          >
            {t("create_layer.property_def")}
          </Typography>
        </Box>
        <Box
          display={"flex"}
          alignItems={"center"}
          color={
            showStyle ? "secondary.main" : darkTheme ? "white" : "darkblue.main"
          }
          paddingY={0.5}
          paddingX={1.5}
          gap={0.5}
          sx={{
            borderBottom: "2px transparent solid",
            borderColor: showStyle ? "secondary.main" : "transparent",
            "@media (hover: hover)": {
              ":hover": {
                borderColor: "secondary.main",
                color: "secondary.main",
                cursor: "pointer",
              },
            },
          }}
          onClick={() => setShowStyle(true)}
        >
          <ColorLensOutlined fontSize="small" />
          <Typography
            textTransform={"uppercase"}
            fontSize={14}
            fontWeight={500}
          >
            {t("create_layer.style_def")}
          </Typography>
        </Box>
      </Box>
      {!showStyle && (
        <Box marginTop={1} display={"flex"} gap={2} flexDirection={"column"}>
          {propertyRows.map((item: Property, index: number) => (
            <Box
              key={`${index}_${item.name}`}
              display={"flex"}
              flexWrap={"wrap"}
              alignItems={"center"}
              sx={{ gap: { xs: 1, md: 2 } }}
            >
              <TextField
                color="primary"
                sx={{
                  width: { xs: "calc(50% - 4px)", md: "25%" },
                }}
                size="small"
                label={t("create_layer.property_name")}
                variant="outlined"
                defaultValue={item.name}
                disabled={
                  !editProperties ||
                  index === 0 ||
                  itemSelected?.editable === false
                }
                onChange={(event) =>
                  handlePropertyRowName(event.target.value, index)
                }
                onBlur={handleCheck}
                onFocus={blockSaveButtonOnFocus}
              />
              <FormControl
                color="primary"
                size="small"
                sx={{ width: { xs: "calc(50% - 52px)", md: "25%" } }}
              >
                <InputLabel>{t("create_layer.data_type")}</InputLabel>
                <Select
                  disabled={
                    !editProperties ||
                    index === 0 ||
                    itemSelected?.editable === false
                  }
                  label={t("create_layer.data_type")}
                  defaultValue={item.datatype}
                  onChange={(event) =>
                    handlePropertyRowDataType(event.target.value, index)
                  }
                >
                  <MenuItem value={mm.LAYERPROPERTYTYPE.STRING}>
                    {t("create_layer.data_type_2")}
                  </MenuItem>
                  <MenuItem value={mm.LAYERPROPERTYTYPE.NUMBER}>
                    {t("create_layer.data_type_3")}
                  </MenuItem>
                  {activeAddLayersListItem !== "create_new_layer" && (
                    <MenuItem value={mm.LAYERPROPERTYTYPE.ENUM}>
                      {t("create_layer.data_type_4")}
                    </MenuItem>
                  )}
                  <MenuItem value={mm.LAYERPROPERTYTYPE.BOOLEAN}>
                    {t("create_layer.data_type_5")}
                  </MenuItem>
                  <MenuItem value={mm.LAYERPROPERTYTYPE.DATE}>
                    {t("create_layer.data_type_6")}
                  </MenuItem>
                  <MenuItem value={mm.LAYERPROPERTYTYPE.INTEGER}>
                    {t("create_layer.data_type_7")}
                  </MenuItem>
                </Select>
              </FormControl>
              {!(
                !editProperties ||
                index === 0 ||
                itemSelected?.editable === false
              ) && (
                <IconButton
                  onClick={() =>
                    monitor(apm, "delete-property | Add", () =>
                      deletePropertyRow(index)
                    )
                  }
                  sx={{
                    color: darkTheme ? "white" : "darkblue.main",
                    "@media (hover: hover)": {
                      ":hover": { color: "secondary.main" },
                    },
                  }}
                >
                  <Delete />
                </IconButton>
              )}
            </Box>
          ))}
          {editProperties && (
            <Button
              variant="outlined"
              sx={{
                width: "fit-content",
                borderColor: darkTheme ? "white" : "darkblue.main",
                color: darkTheme ? "white" : "darkblue.main",
              }}
              onClick={() => monitor(apm, "add-property | Add", addPropertyRow)}
            >
              <AddOutlined fontSize="small" />
              {t("manage_layers.button_add")}
            </Button>
          )}
        </Box>
      )}
      {showStyle && (
        <Box
          marginTop={1}
          paddingX={1}
          display={"flex"}
          flexWrap={"wrap"}
          gap={2}
        >
          <Box
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
            flexDirection={"column"}
            gap={2}
            padding={2}
            borderRadius={"8px"}
            boxShadow={2}
            color={darkTheme ? "white" : "darkblue.main"}
            sx={{
              aspectRatio: "1 / 1",
              width: { xs: "calc(40% - 10px)", md: "calc(20% - 16px)" },
            }}
          >
            {layerDesign}
          </Box>
          {styles &&
            styles.map(
              (
                item: any,
                index: number // TODO: Type
              ) => (
                <Box
                  key={index}
                  display={"flex"}
                  flexDirection={"column"}
                  gap={2}
                  padding={2}
                  borderRadius={"8px"}
                  boxShadow={2}
                  sx={{
                    aspectRatio: "1 / 1",
                    width: { xs: "calc(40% - 10px)", md: "calc(20% - 16px)" },
                  }}
                >
                  <Typography
                    color={darkTheme ? "white" : "darkblue.main"}
                    fontSize={16}
                  >
                    {item.symbolizer === "point" &&
                      t("create_layer.style_type_1")}
                    {item.symbolizer === "line" &&
                      t("create_layer.style_type_2")}
                    {item.symbolizer === "polygon" &&
                      t("create_layer.style_type_3")}
                  </Typography>
                  <Box
                    display={"flex"}
                    flexDirection={"row"}
                    justifyContent={"space-between"}
                    gap={2}
                  >
                    <TextField
                      sx={{
                        width: "50%",
                      }}
                      type="color"
                      size="small"
                      label={t("create_layer.style_color")}
                      variant="outlined"
                      defaultValue={
                        item.symbolizer === "polygon"
                          ? rgbToHex(
                              item.params.fill[0],
                              item.params.fill[1],
                              item.params.fill[2]
                            )
                          : rgbToHex(
                              item.params.stroke[0],
                              item.params.stroke[1],
                              item.params.stroke[2]
                            )
                      }
                      onChange={(event) =>
                        handleColor(item, event.target.value)
                      }
                    />
                    {item.symbolizer === "point" && (
                      <FormControl
                        color="primary"
                        size="small"
                        sx={{ width: "50%" }}
                      >
                        <InputLabel>{t("create_layer.style_shape")}</InputLabel>
                        <Select
                          label={t("create_layer.style_shape")}
                          defaultValue={
                            item.params["font-marker"]
                              ? item.params["font-marker"].name
                              : styles.length === 1
                                ? "circle"
                                : -1
                          }
                          onChange={(event) =>
                            handleShape(item, event.target.value)
                          }
                        >
                          {styles.length > 1 && (
                            <MenuItem key={"none"} value={-1}>
                              -
                            </MenuItem>
                          )}
                          {shapes.map((shape: Shapes) => (
                            <MenuItem key={shape.value} value={shape.value}>
                              {shape.icon}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Box>
                  {/* Add different sliders */}
                  <Box display={"flex"} flexDirection={"column"}>
                    <Typography
                      fontSize={14}
                      color={darkTheme ? "white" : "darkblue.main"}
                    >
                      {t("create_layer.style_transparency")}
                    </Typography>
                    <Slider
                      sx={{ color: "darkblue.main" }}
                      aria-label="Small steps"
                      defaultValue={
                        item.symbolizer === "polygon"
                          ? 100 - item.params.fill[3] * 100
                          : 100 - item.params.stroke[3] * 100
                      }
                      onChange={(event, value: number | number[]) =>
                        handleTransparency(item, Number(value))
                      }
                      value={
                        item.symbolizer === "polygon"
                          ? 100 - item.params.fill[3] * 100
                          : 100 - item.params.stroke[3] * 100
                      }
                    />
                  </Box>
                  {item.symbolizer === "point" && (
                    <Box display={"flex"} flexDirection={"column"}>
                      <Typography
                        fontSize={14}
                        color={darkTheme ? "white" : "darkblue.main"}
                      >
                        {t("create_layer.style_size")}
                      </Typography>
                      <Slider
                        sx={{ color: "darkblue.main" }}
                        aria-label="Small steps"
                        defaultValue={item.params.size}
                        min={8}
                        max={40}
                        onChange={(event, value: number | number[]) =>
                          handleSize(item, Number(value))
                        }
                        value={item.params.size}
                      />
                    </Box>
                  )}
                  {item.symbolizer === "line" && (
                    <Box display={"flex"} flexDirection={"column"}>
                      <Typography
                        fontSize={14}
                        color={darkTheme ? "white" : "darkblue.main"}
                      >
                        {t("create_layer.style_width")}
                      </Typography>
                      <Slider
                        sx={{ color: "darkblue.main" }}
                        min={1}
                        max={20}
                        aria-label="Small steps"
                        defaultValue={item.params["stroke-width"]}
                        onChange={(event, value: number | number[]) =>
                          handleSize(item, Number(value))
                        }
                        value={item.params["stroke-width"]}
                      />
                    </Box>
                  )}
                </Box>
              )
            )}
        </Box>
      )}
    </Box>
  );
}
